import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Navbar from "../components/Navbar";
import VippsButton from "../components/VippsButton";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Globe, MessageSquare, Sparkles, Timer, Shield, Star, RefreshCw } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/30208099/pexels-photo-30208099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Landing() {
  const { loginWithVipps, user } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* When logged in, show the full app navbar with Learn/Practice/Mock/Signs/Tutor links.
          When not logged in, show the lightweight marketing header below. */}
      {user ? (
        <Navbar />
      ) : (
        <header className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-fjord text-white grid place-items-center font-bold font-heading">V</div>
              <span className="font-heading font-bold text-lg">VeiKlar</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-1 me-2 bg-gray-100 rounded-full p-1">
                {[["en","EN"],["so","SO"],["ar","ع"]].map(([code,label])=>(
                  <button key={code} data-testid={`hero-lang-${code}`} onClick={()=>setLang(code)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${lang===code?"bg-fjord text-white":"text-gray-700 hover:bg-white"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <Button onClick={loginWithVipps} data-testid="hero-sign-in" className="bg-fjord hover:bg-fjord-700">
                {t("signIn")}
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="stagger">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-fjord/5 text-fjord text-xs font-semibold rounded-full mb-6">
              <Check className="w-3.5 h-3.5"/> {t("sourceBadge")}
            </div>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              {t("tagline")}
            </h1>
            <p className="text-lg text-gray-600 max-w-xl leading-relaxed mb-8">
              {t("heroSubtitle")}
            </p>
            <div className="flex flex-col gap-3">
              <VippsButton
                onClick={loginWithVipps}
                size="lg"
                className="w-full sm:w-auto"
              />
              <div className="flex flex-wrap gap-3">
                <Button size="lg" variant="ghost" data-testid="hero-cta-secondary"
                  onClick={()=>document.getElementById("features")?.scrollIntoView({behavior:"smooth"})}>
                  {t("learnMore")}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                Most Norwegians use Vipps · <button onClick={() => navigate("/pricing")} className="underline hover:text-gray-600">See pricing</button>
              </p>
            </div>
            <ul className="mt-10 space-y-3 text-sm text-gray-700">
              {[t("features1"),t("features2"),t("features3"),t("features4")].map((f,i)=>(
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-fjord shrink-0 mt-0.5"/> <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
              <img src={HERO_IMG} alt="Norwegian road" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-fjord/60 via-transparent to-transparent"/>
              <div className="absolute bottom-6 inset-x-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80 mb-1">førerprøve B1</div>
                <div className="font-heading text-2xl font-semibold">Norway theory test</div>
              </div>
            </div>
            <Card className="absolute -bottom-6 -start-6 p-4 bg-white shadow-lg max-w-xs hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-fjord/10 text-fjord grid place-items-center">
                  <Timer className="w-5 h-5"/>
                </div>
                <div>
                  <div className="font-semibold text-sm">45 questions · 90 min</div>
                  <div className="text-xs text-gray-500">38 correct to pass</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10">
            {t("learn")} · {t("practice")} · {t("mockExam")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {icon: <Sparkles className="w-5 h-5"/>, title: t("learnTitle"), desc: t("learnSubtitle")},
              {icon: <Timer className="w-5 h-5"/>, title: t("mockTitle"), desc: t("mockSubtitle")},
              {icon: <MessageSquare className="w-5 h-5"/>, title: t("tutorTitle"), desc: t("tutorSubtitle")},
            ].map((f,i)=>(
              <Card key={i} className="p-6 bg-white border border-gray-200">
                <div className="w-10 h-10 rounded-md bg-fjord text-white grid place-items-center mb-4">{f.icon}</div>
                <h3 className="font-heading text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & social proof strip */}
      <section className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { icon: Shield, color: "text-green-600 bg-green-50", title: "Sikkert og trygt", body: "Betaling via Vipps. Kortinfo lagres aldri hos oss." },
              { icon: RefreshCw, color: "text-fjord bg-fjord/5", title: "14 dagers angrerett", body: "Ikke fornøyd? Kontakt oss innen 14 dager for full refusjon." },
              { icon: Star, color: "text-yellow-600 bg-yellow-50", title: "Ingen automatisk trekk", body: "Du bestemmer selv når du vil fornye. Aldri uventede kostnader." },
              { icon: Check, color: "text-purple-600 bg-purple-50", title: "Offisielle spørsmål", body: "Basert på Statens vegvesen og gjeldende norsk trafikkovervåkning." },
            ].map(({ icon: Icon, color, title, body }) => (
              <div key={title} className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg ${color} grid place-items-center shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{title}</div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-8 text-center">
            <p className="text-sm text-gray-500 mb-4 font-medium">Pris: <span className="text-gray-900 font-bold">79 kr / måned</span> · Ingen automatisk fornyelse</p>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-fjord text-white font-semibold rounded-xl px-6 py-2.5 text-sm hover:bg-fjord/90 transition-colors"
            >
              Se priser og kom i gang →
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 py-10 px-4 sm:px-6 lg:px-8 text-sm text-gray-500">
        <div className="max-w-4xl mx-auto">
          <p data-testid="official-notice" className="text-xs leading-relaxed mb-3">{t("officialNotice")}</p>
          <p className="text-center mt-4">VeiKlar · Norway B1 driving theory · Made for learners in Norway</p>
          <div className="flex justify-center gap-4 mt-3 text-xs">
            <a href="/privacy" className="hover:text-gray-700 underline underline-offset-2">Personvern</a>
            <span>·</span>
            <a href="/terms" className="hover:text-gray-700 underline underline-offset-2">Vilkår</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
